import React, { Component } from "react";
import { Select, Button, Popconfirm, Input, Form, Tooltip, Modal, InputNumber } from "antd";
import { overtimeService } from './../../redux/services';
import { allowedTypesByColumn, filterSectionsByColumnType } from "../../utils/integrationUtils";

const ColumnEnums = {};
const TypeEnums = {};

const mkRow = (over = {}) => ({
  column: '',
  eventCode: '',
  type: '',
  limit: 1,
  separator: '',
  ...over,
});

// normaliza valores de uma linha recebida (evita undefined/null)
const normalizeRow = (row = {}) => mkRow({
  column: row.column ?? '',
  eventCode: row.eventCode ?? '',
  type: row.type ?? '',
  limit: (row.limit ?? 1),
  separator: row.separator ?? '',
});

const buildLabelMap = (sections) => {
  const labels = {};
  (sections ?? []).forEach((section) => (section.options ?? []).forEach((option) => {
    labels[option.value] = option.label;
  }));
  return labels;
};

const allowedValorTypes = new Set([
  "DATA_YYYY_MM_DD",
  "DATA_YYYYMMDD",
  "DATA_DD_MM_AAAA",
  "DATA_DDMMAAAA",
  "DATA_DD_MM_AA",
  "DATA_MM_DD_AA",
  "DATA_MMM_DD_YYYY",
  "DATA_DD_MMM_YYYY",
  "DATA_DD_MMMM_YYYY",
]);

class ColumnTable extends Component {
  formRef = React.createRef();

  constructor(props) {
    super(props);

    const initialRows = Array.isArray(props.eventData) && props.eventData.length
      ? props.eventData.map(normalizeRow)
      : [mkRow()];

    this.state = {
      rows: initialRows,
      columnOptions: [],
      typeSections: [],
    };
  }

  componentDidMount() {
    this.getFormData();
  };

  componentDidUpdate(prevProps, prevState) {
    // se o pai atualizar eventData (ex.: chegou do backend), sincroniza aqui
    if (prevProps.eventData !== this.props.eventData) {
      const incoming = Array.isArray(this.props.eventData)
        ? this.props.eventData.map(normalizeRow)
        : [];
      this.setState({
        rows: incoming.length ? incoming : [mkRow()],
      });
    }

    const { rows, typeSections, columnOptions } = this.state;

    if (this.props.onRowsChange && (prevState.rows !== rows || prevState.typeSections !== typeSections || prevState.columnOptions !== columnOptions)) {
      this.props.onRowsChange(rows, typeSections, columnOptions);
    }
  }

  handleAddRow = () => {
    this.setState((prevState) => ({
      rows: [...prevState.rows, mkRow()],
    }));
  };

  handleRemoveRow = (index) => {
    this.setState((prevState) => {
      const next = prevState.rows.filter((_, i) => i !== index);
      // garante pelo menos 1 linha
      return { rows: next.length ? next : [mkRow()] };
    });
  };

  handleColumnChange = (value, index) => {
    this.setState((prevState) => ({
      rows: prevState.rows.map((row, i) => {
          if (i === index) {
            const hasChangedColumn = row.column !== value;
            const defaultType = row.type && row.type !== '' && !hasChangedColumn ? row.type : allowedTypesByColumn[value]?.defaultValue || undefined;
            return { ...row, column: value, /* reset campo dependente se precisar: */ type: defaultType }
          } else {
            return row;
          }
        }
      ),
    }));
  };

  handleTypeChange = (value, index) => {
    this.setState((prevState) => ({
      rows: prevState.rows.map((row, i) =>
        i === index ? { ...row, type: value } : row
      ),
    }));
  };

  handleEventCodeChange = (value, index) => {
    this.setState((prevState) => ({
      rows: prevState.rows.map((row, i) =>
        i === index ? { ...row, eventCode: value } : row
      ),
    }));
  };

  handleLimitChange = (index, value) => {
    this.setState((prevState) => ({
      rows: prevState.rows.map((row, i) =>
        i === index ? { ...row, limit: value ?? 1 } : row
      ),
    }));
  }

  handleSeparatorChange = (index, event) => {
    const { value } = event.target;
    this.setState((prevState) => ({
      rows: prevState.rows.map((row, i) =>
        i === index ? { ...row, separator: value } : row
      ),
    }));
  }

  getFormData = async () => {
    const currentRows = Array.isArray(this.state?.rows) && this.state.rows.length
      ? this.state.rows
      : [mkRow()];

    try {
      const [colRes, typeRes] = await Promise.all([
        overtimeService.getColumnItems(),
        overtimeService.getTypesValue(),
      ]);

      const columnOptions = (colRes?.data ?? [])
        .filter(sec => sec?.title && Array.isArray(sec?.data))
        .map(sec => ({
          title: sec.title,
          options: (sec.data ?? [])
            .filter(i => (i?.label || i?.name) && i?.value != null)
            .map(i => ({
              value: i.value,
              label: i.label ?? i.name
            })),
        }));

      const typeSections = (typeRes?.data ?? [])
        .filter(sec => sec?.title && Array.isArray(sec?.data))
        .map(sec => ({
          title: sec.title,
          options: (sec.data ?? [])
            .filter(i => (i?.label || i?.name) && i?.value != null)
            .map(i => ({
              value: i.value,
              label: i.label ?? i.name,
              example: i.example ?? i.exemple,
            })),
        }));

      const normalizedRows = currentRows.map(normalizeRow);

      this.setState({
        columnOptions,
        typeSections,
        rows: normalizedRows.length ? normalizedRows : [mkRow()],
      });

      return normalizedRows.map(row => ({
        column: row.column,
        eventCode: row.eventCode,
        type: row.type,
        limit: row.limit,
        separator: row.separator,
      }));
    } catch (err) {
      Modal.error({
        title: 'Erro ao carregar dados',
        content: String(err),
      });

      const safeRows = currentRows.map(normalizeRow);

      return safeRows.map(row => ({
        column: row.column,
        eventCode: row.eventCode,
        type: row.type,
        limit: row.limit,
        separator: row.separator,
      }));
    }
  };

  renderReadOnly() {
    const { rows, columnOptions, typeSections } = this.state;
    const columnLabels = buildLabelMap(columnOptions);
    const typeLabels = buildLabelMap(typeSections);

    return (
      <Form ref={this.formRef} component={false}>
        <div className="integration-table integration-table-columns is-read-only">
          <div className="integration-table-header">
            <span>#</span>
            <span>Coluna</span>
            <span>Formato do Valor</span>
            <span>Tamanho Campo</span>
            <span>Separador de Coluna</span>
          </div>
          {rows.map((row, index) => {
            const type = row.type || allowedTypesByColumn[row.column]?.defaultValue;

            return (
              <div className="integration-table-row" key={index}>
                <span className="integration-table-index">{String(index + 1).padStart(2, "0")}</span>
                <span>{columnLabels[row.column] ?? (row.column || "-")}</span>
                <span>{row.column === "TEXTO_FIXO" ? (row.eventCode || "-") : (typeLabels[type] ?? (type || "-"))}</span>
                <span>{row.limit}</span>
                <span>{row.separator || "-"}</span>
              </div>
            );
          })}
        </div>
      </Form>
    );
  }

  render() {
    const { rows, columnOptions, typeSections } = this.state;
    const { readOnly } = this.props;

    if (readOnly) return this.renderReadOnly();

    return (
      <Form ref={this.formRef} component={false}>
        <div className={`integration-table integration-table-columns ${readOnly ? "is-read-only" : ""}`}>
          <div className="integration-table-header">
            <span className="required">Coluna</span>
            <span className="required">Formato do Valor</span>
            <span className="required">Tamanho Campo</span>
            <span>
              Separador de Coluna{" "}
              <Tooltip title="Caractere adicionado logo após o valor da coluna (ex.: ;).">
                <i className="fal fa-info-circle" />
              </Tooltip>
            </span>
            {!readOnly && <span className="integration-table-actions">Ações</span>}
          </div>
          {rows.map((row, index) => (
            <div className="integration-table-row" key={index}>
              <Select
                showSearch
                placeholder="Selecione uma coluna"
                value={row.column || undefined}
                onChange={(value) => this.handleColumnChange(value, index)}
                disabled={readOnly}
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {(columnOptions ?? []).map((sec) => (
                  <Select.OptGroup key={sec.title} label={sec.title}>
                    {(sec.options ?? []).map((t) => (
                      <Select.Option key={t.value} value={t.value}>
                        <Tooltip title={t.example}>
                          <div style={{ width: "100%" }}>{t.label}</div>
                        </Tooltip>
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                ))}
              </Select>

              {row.column === "TEXTO_FIXO" ? (
                <Input
                  placeholder="Digite um texto"
                  value={row.eventCode}
                  onChange={(e) => this.handleEventCodeChange(e.target.value, index)}
                  disabled={readOnly}
                />
              ) : (
                <Select
                  placeholder="Selecione o formato"
                  // se for VALOR e o tipo atual não é permitido, zera o value pra evitar opção inválida
                  value={
                    // row.column === "VALOR" && row.type && !allowedValorTypes.has(row.type)
                    //   ? undefined
                    //   : row.type
                    row.type && row.type !== '' ? row.type : allowedTypesByColumn[row.column]?.defaultValue || undefined
                  }
                  onChange={(value) => this.handleTypeChange(value, index)}
                  disabled={readOnly}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.children ?? "")
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {(
                    (typeSections ?? [])
                      .map((sec) => {
                        const filteredOptions = filterSectionsByColumnType(row.column, sec.options, 'options');
                        return { ...sec, options: filteredOptions };
                      })
                      // remove grupos vazios
                      .filter((sec) => (sec.options ?? []).length > 0)
                  ).map((sec) => (
                    <Select.OptGroup key={sec.title} label={sec.title}>
                      {(sec.options ?? []).map((t) => (
                        <Select.Option key={t.value} value={t.value}>
                          <Tooltip title={t.example}>
                            <div style={{ width: "100%" }}>{t.label}</div>
                          </Tooltip>
                        </Select.Option>
                      ))}
                    </Select.OptGroup>
                  ))}
                </Select>
              )}

              <InputNumber
                style={{ width: "100%" }}
                min={1}
                value={row.limit}
                onChange={(val) => this.handleLimitChange(index, val)}
                disabled={readOnly}
              />

              <Input
                value={row.separator}
                maxLength={1}
                onChange={(event) => this.handleSeparatorChange(index, event)}
                readOnly={readOnly}
              />

              {!readOnly && (
                <div className="integration-table-actions">
                  <Popconfirm
                    title="Deseja excluir essa linha?"
                    onConfirm={() => this.handleRemoveRow(index)}
                    okText="Sim"
                    cancelText="Não"
                  >
                    <Button
                      className="integration-table-remove"
                      danger
                      icon={<i className="fal fa-trash" />}
                    />
                  </Popconfirm>
                </div>
              )}
            </div>
          ))}
        </div>
        {!readOnly && (
          <Button
            className="integration-table-add"
            type="secondary"
            onClick={this.handleAddRow}
            icon={<i className="far fa-plus" />}
          >
            Adicionar coluna
          </Button>
        )}
      </Form>
    );
  }
}

export default ColumnTable;
